/**
 * SDK 日志级别类型
 * 表示日志输出的最低级别
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/**
 * 日志器接口
 * 提供按级别输出日志的方法
 */
export interface Logger {
  level: LogLevel
  debug: (...args: any[]) => void
  info: (...args: any[]) => void
  warn: (...args: any[]) => void
  error: (...args: any[]) => void
}

/**
 * WebSocket 客户端配置
 */
export interface WSOptions {
  /** 服务地址，例如 ws://localhost:3152 */
  url: string
  /** 可选的子协议列表 */
  protocols?: string[]
  /** 自动重连配置 */
  retry?: { retries: number; interval: number }
  /** 心跳配置 */
  heartbeat?: { interval: number; timeout: number; payload?: any }
  /** 编解码方式：msgpack 或 json */
  codec?: 'msgpack' | 'json'
}

/**
 * 模板渲染选项
 */
export interface RenderOptions {
  format?: 'png' | 'jpeg'
  quality?: number
  multiplier?: number
  colorMode?: 'color' | 'mono'
}

/**
 * 打印选项
 */
export interface PrintOptions {
  /** 打印机名称 */
  printer?: string
  /** 份数 */
  copies?: number
  /** 纸张宽度（单位由后端决定） */
  width?: number
  /** 纸张高度（单位由后端决定） */
  height?: number
  /** 并发打印数 */
  concurrency?: number
  /** 图片格式 */
  format?: 'png' | 'jpeg'
}

/**
 * 打印任务描述
 */
export interface PrintJob {
  /** 模板 JSON */
  template?: any
  /** 数据 JSON */
  data?: any
  /** 直接打印的图片数据（无需渲染） */
  images?: Uint8Array[]
  /** 打印选项 */
  options?: PrintOptions
}

/**
 * 打印机信息
 */
export interface PrinterInfo {
  name: string
  isDefault?: boolean
}

/**
 * SDK 总体配置
 */
export interface SDKConfig {
  ws: WSOptions
  logger?: Logger
  renderer?: (tpl: any, data: any, opts: RenderOptions) => Promise<Uint8Array>
}

export type MessageHandler = (msg: any) => void

/**
 * WebSocket 客户端接口
 */
export interface Client {
  connect: () => Promise<void>
  disconnect: () => void
  isConnected: () => boolean
  send: (type: string, payload?: any) => void
  on: (event: 'connected' | 'disconnected' | 'error' | 'message', handler: Function) => void
  off: (event: 'connected' | 'disconnected' | 'error' | 'message', handler: Function) => void
}

/**
 * 打印管理器接口
 */
export interface PrintManager {
  enqueue: (job: PrintJob) => void
  start: () => Promise<void>
  pause: () => void
  clear: () => void
  onStatus: (handler: (status: { type: string; data?: any }) => void) => void
  listPrinters: () => void
  setDefaults: (opts: PrintOptions) => void
  printImages: (images: Uint8Array[], opts?: PrintOptions) => void
}

/**
 * SDK 版本号
 */
export const SDK_VERSION = '1.0.0'
